<template>
  <div class="contact-detail-phone">
    <div class="d-flex align-center justify-space-between mb-2">
      <h4 class="text-subtitle-1 d-flex align-center">
        <v-icon start :color="phoneTypeConfig.color">{{
          phoneTypeConfig.icon
        }}</v-icon>
        Telefoonnummer
      </h4>
      <v-btn
        icon
        variant="text"
        size="small"
        :color="disabled ? 'success' : 'error'"
        :title="
          disabled ? 'Telefoonnummer herstellen' : 'Telefoonnummer verwijderen'
        "
        @click="$emit('remove')"
      >
        <v-icon>{{ disabled ? "mdi-undo" : "mdi-delete" }}</v-icon>
      </v-btn>
    </div>

    <v-text-field
      :model-value="modelValue.phone_number"
      label="Telefoonnummer"
      placeholder="06xxxxxxxx of 020xxxxxxx"
      variant="outlined"
      density="comfortable"
      :rules="[rules.required, rules.phoneNumber]"
      prepend-inner-icon="mdi-phone"
      class="mb-3"
      :disabled="disabled"
      @update:model-value="updatePhoneNumber"
      @input="validatePhoneNumber"
    />

    <v-select
      :model-value="modelValue.phone_type"
      :items="phoneTypeOptions"
      label="Type telefoonnummer"
      variant="outlined"
      item-title="label"
      item-value="value"
      density="comfortable"
      :rules="[rules.required]"
      class="mb-3"
      :disabled="disabled"
      @update:model-value="updatePhoneType"
    >
      <template #prepend-inner>
        <v-icon :icon="phoneTypeConfig.icon" :color="phoneTypeConfig.color" />
      </template>

      <template #item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps">
          <template #prepend>
            <v-icon :icon="item.raw.icon" :color="item.raw.color" />
          </template>
        </v-list-item>
      </template>
    </v-select>

    <v-textarea
      :model-value="modelValue.meta"
      label="Extra informatie (optioneel)"
      placeholder="Bijvoorbeeld: Best bereikbaar tussen 9-17u, WhatsApp beschikbaar"
      variant="outlined"
      density="comfortable"
      rows="2"
      auto-grow
      prepend-inner-icon="mdi-information"
      class="mb-3"
      counter="100"
      :rules="[rules.maxLength(100)]"
      :disabled="disabled"
      @update:model-value="updateMeta"
    />

    <!-- Phone validation feedback -->
    <v-alert
      v-if="phoneValidationMessage && !disabled"
      :type="phoneValidationType"
      variant="tonal"
      class="mb-3"
      :text="phoneValidationMessage"
    />

    <div v-if="showPrimary" class="d-flex align-center">
      <v-switch
        :model-value="modelValue.primary"
        label="Primair telefoonnummer"
        color="primary"
        hide-details
        :disabled="disabled"
        @update:model-value="updatePrimary"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  showPrimary: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "remove"]);

// Reactive data
const phoneValidationMessage = ref("");
const phoneValidationType = ref("info");

// Phone type options
const phoneTypeOptions = [
  {
    label: "Mobiel",
    value: "mobile",
    icon: "mdi-cellphone",
    color: "green",
  },
  {
    label: "Vast",
    value: "vast",
    icon: "mdi-phone",
    color: "blue",
  },
  {
    label: "Fax",
    value: "fax",
    icon: "mdi-fax",
    color: "orange",
  },
  {
    label: "Overig",
    value: "overig",
    icon: "mdi-phone-outline",
    color: "grey",
  },
];

const phoneTypeConfig = computed(() => {
  return (
    phoneTypeOptions.find((opt) => opt.value === props.modelValue.phone_type) ||
    phoneTypeOptions[0]
  );
});

const rules = {
  required: (value) => !!value || "Telefoonnummer is verplicht",
  phoneNumber: (value) => {
    if (!value) return "Telefoonnummer is verplicht";

    // Dutch phone validation patterns
    const patterns = {
      mobile: /^(06\d{8}|(\+31|0031)6\d{8})$/,
      landline: /^(0\d{9}|(\+31|0031)\d{9})$/,
      flexible: /^[\d\s\-().+]{4,15}$/, // Allow 4-15 characters for short service numbers
    };

    const cleaned = value.replace(/[\s\-().]/g, "");

    if (
      patterns.mobile.test(cleaned) ||
      patterns.landline.test(cleaned) ||
      patterns.flexible.test(cleaned)
    ) {
      return true;
    }

    return "Voer een geldig Nederlands telefoonnummer in";
  },
  maxLength: (max) => (value) => {
    return (
      !value || value.length <= max || `Maximaal ${max} karakters toegestaan`
    );
  },
};

const validatePhoneNumber = () => {
  if (!props.modelValue.phone_number) {
    phoneValidationMessage.value = "";
    return;
  }

  const cleaned = props.modelValue.phone_number.replace(/[\s\-().]/g, "");

  // Mobile number detection
  if (/^(06\d{8}|(\+31|0031)6\d{8})$/.test(cleaned)) {
    phoneValidationMessage.value = "Mobiel telefoonnummer gedetecteerd";
    phoneValidationType.value = "success";
    if (props.modelValue.phone_type !== "mobile") {
      updatePhoneType("mobile");
    }
    return;
  }

  // Landline detection
  if (/^(0\d{9}|(\+31|0031)\d{9})$/.test(cleaned)) {
    phoneValidationMessage.value = "Vast telefoonnummer gedetecteerd";
    phoneValidationType.value = "success";
    if (props.modelValue.phone_type !== "vast") {
      updatePhoneType("vast");
    }
    return;
  }

  // Other valid format
  if (/^[\d\s\-().+]{4,15}$/.test(props.modelValue.phone_number)) {
    phoneValidationMessage.value = "Telefoonnummer geaccepteerd als 'Overig'";
    phoneValidationType.value = "info";
    if (props.modelValue.phone_type !== "overig") {
      updatePhoneType("overig");
    }
    return;
  }

  phoneValidationMessage.value = "Ongeldig telefoonnummer formaat";
  phoneValidationType.value = "error";
};

const updatePhoneNumber = (value) => {
  emit("update:modelValue", {
    ...props.modelValue,
    phone_number: value,
  });
};

const updatePhoneType = (value) => {
  emit("update:modelValue", {
    ...props.modelValue,
    phone_type: value,
  });
};

const updateMeta = (value) => {
  emit("update:modelValue", {
    ...props.modelValue,
    meta: value,
  });
};

const updatePrimary = (value) => {
  emit("update:modelValue", {
    ...props.modelValue,
    primary: value,
  });
};
</script>

<style scoped>
.contact-detail-phone {
  border-left: 3px solid rgb(var(--v-theme-green));
  padding-left: 16px;
}
</style>
